// SEASONAL_DECOR_v1 (08.09.2026, Toby: "Shearly zu Anlässen dezent, aber sichtbar dekorieren", Vorlage
// = Vorschau-Artifact vom 07.09. abends, Endstand "gut so") -- Saisonale Dekoration NUR im Kalender.
// Konfig-Liste: Anlass -> Zeitraum (je Jahr, Wiesn-Termine wandern!) -> Badge + Eck-Element + Klammer +
// Abwesenheits-Emoji. Datumsgesteuert: außerhalb des Zeitraums rendert alles null.
// Regeln (Toby 07.09.): Badge = Standard-Emoji + Schriftzug; Abwesenheits-Symbol = Standard-Emoji, KEIN
// Zusatztext; Lebkuchenherz + Wäscheklammer bleiben eigene Grafik; nichts im Dashboard, nichts über
// Terminen/Namen/Knöpfen; keine Deko im Druck (index.html: @media print .shearly-seasonal { display:none }).
// Weitere Anlässe (Weihnachten, Fasching, Ostern, Starkbierzeit, Maibaum) erst nach der Wiesn ergänzen --
// je Anlass nur Badge + 1 Corner + 1 Abwesenheits-Emoji, gleiche Sparsamkeit.
import { useIsMobile } from "../core/config.jsx";

const _wiesn = (year, from, to) => ({
  id: "wiesn-" + year, from, to,
  badge: { emoji: "🥨", text: "O'zapft is!", sub: "Wiesn " + _short(from) + "–" + _short(to) },
  absenceEmoji: "🍻",
  corner: "lebkuchenherz", peg: "difigiano",
});
function _short(iso) { const [, m, d] = iso.split("-"); return Number(d) + "." + Number(m) + "."; }

export const SEASONS = [
  // Oktoberfest-Termine: 2026 = 19.09.–04.10., 2027 = 18.09.–03.10. (jedes Jahr nachtragen)
  // SEASONAL_DECOR_BOOKING_v1: gleiche Zeiträume auch in static/booking.html (SEASONS) nachtragen
  _wiesn(2026, "2026-09-19", "2026-10-04"),
  _wiesn(2027, "2027-09-18", "2027-10-03"),
];

const _key = (d) => {
  if (!d) return "";
  if (typeof d === "string") return d.slice(0, 10);
  return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
};
// Vorschau/Sicht-OK außerhalb des Zeitraums: ?season=wiesn-2026 an die App-URL hängen (merkt sich der Browser
// per localStorage), ?season=off setzt es zurück. Nur Anzeige, keine Serverwirkung.
const _forced = (() => {
  try {
    const q = new URLSearchParams(window.location.search).get("season");
    if (q) localStorage.setItem("sp_season_preview", q);
    const v = localStorage.getItem("sp_season_preview");
    return (v && v !== "off") ? v : null;
  } catch (e) { return null; }
})();
export const dateInSeason = (season, d) => {
  if (!season) return false;
  if (_forced && season.id === _forced) return true;
  const k = _key(d); return k >= season.from && k <= season.to;
};
export const activeSeason = (d) => {
  if (_forced) { const f = SEASONS.find(s => s.id === _forced); if (f) return f; }
  return SEASONS.find(s => dateInSeason(s, d)) || null;
};

// Abwesenheit fürs 🍻: automatische Urlaubs-Blocker (source vacation_auto) und Blocker vom Typ "Urlaub"
// (BlockerButton speichert "Typ: Notiz"). "Feierabend" / "früher gehen" haben keinen eigenen Blocker-Typ --
// deshalb nur die exakte Formulierung am Anfang, keine freie Textsuche (Toby: nichts Falsches soll ein 🍻 bekommen).
export const isSeasonAbsence = (apt) => {
  if (!apt) return false;
  if (apt.source === "vacation_auto") return true;
  if (apt.source !== "blocker") return false;
  const n = String(apt.notes || "");
  const type = n.split(":")[0].trim().toLowerCase();
  if (type === "urlaub") return true;
  return /^(?:[^:]*:\s*)?(feierabend|fr(?:ü|ue)her gehen|fr(?:ü|ue)her feierabend)\b/i.test(n);
};

// --- Badge neben dem Titel "Kalender" (eine Zeile, nie umbrechen; bei Platzmangel nur Emoji + Kurztext) ---
export const SeasonBadge = ({ season }) => {
  const isMobile = useIsMobile();
  if (!season) return null;
  return (
    <span className="shearly-seasonal" title={season.badge.text + " · " + season.badge.sub} style={{
      display: "inline-flex", alignItems: "center", gap: 6, marginLeft: isMobile ? 0 : 10, verticalAlign: "middle",
      padding: "3px 10px 3px 6px", borderRadius: 999, whiteSpace: "nowrap",
      background: "linear-gradient(90deg, rgba(30,111,217,0.22), rgba(30,111,217,0.06))",
      border: "1px solid rgba(59,139,234,0.45)",
      fontFamily: '"Grenze Gotisch", Georgia, serif', fontSize: isMobile ? 13 : 15, fontWeight: 500,
      letterSpacing: "0.01em", color: "var(--text)", lineHeight: 1.2,
    }}>
      <span role="img" aria-label="Brezn" style={{ fontSize: isMobile ? 15 : 17, lineHeight: 1 }}>{season.badge.emoji}</span>
      <span>{season.badge.text}</span>
      {!isMobile && <small style={{ fontFamily: "-apple-system, 'Segoe UI', Helvetica, Arial, sans-serif", fontSize: 11, fontWeight: 500, color: "var(--textLight)" }}>{season.badge.sub}</small>}
    </span>
  );
};

// --- Lebkuchenherz rechts neben den Tages-Chips (eigenes SVG aus der Vorschau) ---
export const SeasonCorner = ({ season }) => {
  if (!season || season.corner !== "lebkuchenherz") return null;
  return (
    <svg className="shearly-seasonal" width="58" height="52" viewBox="0 0 92 82" aria-label="Lebkuchenherz O'zapft is"
         style={{ flexShrink: 0, marginLeft: 8, opacity: 0.95, filter: "drop-shadow(0 4px 10px rgba(0,0,0,0.35))", pointerEvents: "none" }}>
      <defs><linearGradient id="shSeasonLg" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#8A4A1C" /><stop offset="1" stopColor="#6B3410" /></linearGradient></defs>
      <path d="M46 76 C20 58 4 44 4 26 4 13 14 4 26 4c9 0 16 5 20 12 4-7 11-12 20-12 12 0 22 9 22 22 0 18-16 32-42 50z" fill="url(#shSeasonLg)" stroke="#3E1D08" strokeWidth="1.5" />
      <path d="M46 68 C25 53 11 41 11 27c0-9 7-15 15-15 8 0 14 5 20 13 6-8 12-13 20-13 8 0 15 6 15 15 0 14-14 26-35 41z" fill="none" stroke="#FFF7EA" strokeWidth="2.2" strokeDasharray="1 4.5" strokeLinecap="round" />
      <text x="46" y="36" textAnchor="middle" fontFamily="Grenze Gotisch, Georgia, serif" fontSize="14" fontWeight="700" fill="#FFF7EA">O'zapft</text>
      <text x="46" y="52" textAnchor="middle" fontFamily="Grenze Gotisch, Georgia, serif" fontSize="14" fontWeight="700" fill="#FFF7EA">is!</text>
      <circle cx="18" cy="14" r="2.4" fill="#3B8BEA" /><circle cx="74" cy="14" r="2.4" fill="#3B8BEA" />
    </svg>
  );
};

// --- Holz-Wäscheklammer "Difigiano" an der Oberkante des Rasters (eigenes SVG aus der Vorschau, "gut so") ---
// Wird in einem 0 px hohen, relativ positionierten Wrapper VOR der Raster-Card gerendert und hängt
// darüber; pointer-events none, damit kein Knopf/Termin darunter blockiert wird.
export const SeasonPeg = ({ season }) => {
  if (!season || season.peg !== "difigiano") return null;
  return (
    <div className="shearly-seasonal" style={{ position: "relative", height: 0, zIndex: 5, pointerEvents: "none" }}>
      <svg width="42" height="67" viewBox="0 0 80 128" aria-label="Wäscheklammer Difigiano"
           style={{ position: "absolute", right: 22, top: -24, filter: "drop-shadow(0 6px 10px rgba(0,0,0,0.45))" }}>
        <defs>
          <linearGradient id="shPegWf" x1="0" x2="1"><stop offset="0" stopColor="#F0D3A2" /><stop offset=".45" stopColor="#D9AE72" /><stop offset="1" stopColor="#A9773F" /></linearGradient>
          <linearGradient id="shPegWb" x1="0" x2="1"><stop offset="0" stopColor="#C79A62" /><stop offset=".5" stopColor="#A97A45" /><stop offset="1" stopColor="#7E5428" /></linearGradient>
          <radialGradient id="shPegSp" cx=".35" cy=".35" r=".8"><stop offset="0" stopColor="#F2F4F6" /><stop offset=".5" stopColor="#AEB5BC" /><stop offset="1" stopColor="#5E666D" /></radialGradient>
        </defs>
        <g transform="rotate(-12 40 50)">
          <g transform="rotate(-7 40 50)">
            <path d="M33 6h14v44l-2 6v60l-5 6-5-6V56l-2-6z" fill="url(#shPegWb)" stroke="#5E3B18" strokeWidth="1.4" strokeLinejoin="round" />
            <path d="M36 12v96M44 14v92" stroke="#7E5428" strokeWidth=".8" opacity=".6" />
          </g>
          <g transform="rotate(7 40 50)">
            <path d="M33 6h14v44l-2 6v60l-5 6-5-6V56l-2-6z" fill="url(#shPegWf)" stroke="#5E3B18" strokeWidth="1.4" strokeLinejoin="round" />
            <path d="M35 10v100" stroke="#FFF3DD" strokeWidth="1.2" opacity=".55" />
            <path d="M45 12v96" stroke="#8B6234" strokeWidth=".9" opacity=".5" />
            <text transform="translate(44 112) rotate(-90)" fontFamily="Grenze Gotisch, Georgia, serif" fontSize="12.5" fontWeight="700" fill="#3E2412" letterSpacing=".04em">Difigiano</text>
          </g>
          <g fill="none" stroke="#8E959C" strokeWidth="2.6" strokeLinecap="round">
            <path d="M26 44c0-6 8-6 8 0s-8 6-8 0" opacity=".9" /><path d="M46 44c0-6 8-6 8 0s-8 6-8 0" opacity=".9" />
            <path d="M30 50l-4 16M50 50l4 16" /><path d="M26 66h8M46 66h8" strokeWidth="2.2" />
          </g>
          <circle cx="40" cy="47" r="7.5" fill="url(#shPegSp)" stroke="#5E666D" strokeWidth="1.6" />
          <circle cx="40" cy="47" r="2.6" fill="#4A5158" />
          <ellipse cx="40" cy="123" rx="16" ry="3" fill="rgba(0,0,0,0.35)" />
        </g>
      </svg>
    </div>
  );
};

// --- 🍻 rechts oben auf Abwesenheitskarten (Standard-Emoji, kein Text) ---
export const SeasonAbsenceMark = ({ season }) => {
  if (!season) return null;
  return (
    <span className="shearly-seasonal" role="img" aria-label="Prost" style={{
      position: "absolute", right: 6, top: 3, fontSize: 16, lineHeight: 1, pointerEvents: "none",
      filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.35))",
    }}>{season.absenceEmoji}</span>
  );
};
